/**
 * Datos fiscales del tenant para DIAN: razón social, NIT + DV, régimen
 * tributario, códigos de responsabilidad y actividad económica (CIIU).
 *
 * Los códigos de responsabilidad vienen como un array JSON en la DB
 * (`responsibility_codes_json`). Acá los editamos como chips: el admin
 * agrega/quita de una lista. Por ahora acepta texto libre porque la lista
 * oficial DIAN es extensa y vamos piloto → piloto.
 *
 * El DV (dígito de verificación) se puede auto-calcular a partir del NIT
 * — ofrecemos el botón para llenarlo automáticamente.
 */

import { Button, CollapsibleSection } from '@atreus/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileBadge, Wand2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { apiPatch } from '../../cloud.js';

interface Props {
  tenantId: string;
  tenant: {
    legalName: string | null;
    nit: string | null;
    dv: string | null;
    regime: 'simplificado' | 'comun';
    responsibilityCodesJson: string;
    economicActivityCode: string | null;
  };
}

/**
 * Calcula el dígito de verificación del NIT colombiano (algoritmo oficial
 * DIAN: suma ponderada módulo 11).
 */
const computeDv = (nit: string): string | null => {
  if (!/^\d+$/.test(nit)) return null;
  const weights = [3, 7, 13, 17, 19, 23, 29, 37, 41, 43, 47, 53, 59, 67, 71];
  const digits = nit.split('').reverse();
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    sum += Number(digits[i]) * (weights[i] ?? 0);
  }
  const mod = sum % 11;
  const dv = mod >= 2 ? 11 - mod : mod;
  return String(dv);
};

const parseCodes = (json: string): string[] => {
  try {
    const v = JSON.parse(json);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
};

const REGIME_LABELS: Record<'simplificado' | 'comun', string> = {
  simplificado: 'Simplificado (No responsable de IVA)',
  comun: 'Común (Responsable de IVA)',
};

export const TenantFiscalSection = ({ tenantId, tenant }: Props) => {
  const qc = useQueryClient();
  const [legalName, setLegalName] = useState(tenant.legalName ?? '');
  const [nit, setNit] = useState(tenant.nit ?? '');
  const [dv, setDv] = useState(tenant.dv ?? '');
  const [regime, setRegime] = useState(tenant.regime);
  const [codes, setCodes] = useState<string[]>(() =>
    parseCodes(tenant.responsibilityCodesJson),
  );
  const [codeDraft, setCodeDraft] = useState('');
  const [economicActivityCode, setEconomicActivityCode] = useState(
    tenant.economicActivityCode ?? '',
  );

  useEffect(() => {
    setLegalName(tenant.legalName ?? '');
    setNit(tenant.nit ?? '');
    setDv(tenant.dv ?? '');
    setRegime(tenant.regime);
    setCodes(parseCodes(tenant.responsibilityCodesJson));
    setEconomicActivityCode(tenant.economicActivityCode ?? '');
  }, [
    tenant.legalName,
    tenant.nit,
    tenant.dv,
    tenant.regime,
    tenant.responsibilityCodesJson,
    tenant.economicActivityCode,
  ]);

  const originalCodes = useMemo(
    () => parseCodes(tenant.responsibilityCodesJson),
    [tenant.responsibilityCodesJson],
  );

  const codesEqual =
    codes.length === originalCodes.length &&
    codes.every((c, i) => c === originalCodes[i]);

  const save = useMutation({
    mutationFn: () =>
      apiPatch<{ ok: true }>(`/admin/tenants/${tenantId}`, {
        legalName: legalName.trim() || null,
        nit: nit.trim() || null,
        dv: dv.trim() || null,
        regime,
        responsibilityCodes: codes,
        economicActivityCode: economicActivityCode.trim() || null,
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['admin', 'tenants', tenantId] }),
  });

  const dirty =
    (legalName.trim() || null) !== (tenant.legalName || null) ||
    (nit.trim() || null) !== (tenant.nit || null) ||
    (dv.trim() || null) !== (tenant.dv || null) ||
    regime !== tenant.regime ||
    !codesEqual ||
    (economicActivityCode.trim() || null) !==
      (tenant.economicActivityCode || null);

  const addCode = (): void => {
    const next = codeDraft.trim().toUpperCase();
    if (!next || codes.includes(next)) {
      setCodeDraft('');
      return;
    }
    setCodes([...codes, next]);
    setCodeDraft('');
  };

  const removeCode = (code: string): void => {
    setCodes(codes.filter((c) => c !== code));
  };

  const autofillDv = (): void => {
    const computed = computeDv(nit.trim());
    if (computed) setDv(computed);
  };

  return (
    <CollapsibleSection
      title="Datos fiscales (DIAN)"
      icon={FileBadge}
      persistKey={`tenant-fiscal-${tenantId}`}
    >
      <p className="mb-3 text-xs text-fg-muted">
        Información tributaria del tenant. Campos requeridos para emitir
        facturación electrónica. La razón social es la que aparece ante la
        DIAN (puede diferir del nombre comercial).
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label
            htmlFor="fisc-legal"
            className="mb-1 block text-sm font-semibold"
          >
            Razón social
          </label>
          <input
            id="fisc-legal"
            autoComplete="off"
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
            placeholder="Ej: RESTOBAR PILOTO S.A.S."
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base"
          />
        </div>
        <div>
          <label
            htmlFor="fisc-nit"
            className="mb-1 block text-sm font-semibold"
          >
            NIT
          </label>
          <input
            id="fisc-nit"
            autoComplete="off"
            inputMode="numeric"
            value={nit}
            onChange={(e) => setNit(e.target.value.replace(/\D/g, ''))}
            placeholder="900123456"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 font-mono text-base"
          />
        </div>
        <div>
          <label
            htmlFor="fisc-dv"
            className="mb-1 block text-sm font-semibold"
          >
            DV{' '}
            <span className="font-normal text-fg-muted">
              (dígito de verificación)
            </span>
          </label>
          <div className="flex gap-2">
            <input
              id="fisc-dv"
              autoComplete="off"
              inputMode="numeric"
              maxLength={1}
              value={dv}
              onChange={(e) => setDv(e.target.value.replace(/\D/g, '').slice(0, 1))}
              placeholder="7"
              className="w-20 rounded-md border border-border bg-surface px-3 py-2 text-center font-mono text-base"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={autofillDv}
              disabled={!nit.trim()}
              title="Calcular DV a partir del NIT"
            >
              <Wand2 className="size-3.5" />
              Calcular
            </Button>
          </div>
        </div>
        <div>
          <label
            htmlFor="fisc-regime"
            className="mb-1 block text-sm font-semibold"
          >
            Régimen
          </label>
          <select
            id="fisc-regime"
            autoComplete="off"
            value={regime}
            onChange={(e) => setRegime(e.target.value as 'simplificado' | 'comun')}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base"
          >
            {(['simplificado', 'comun'] as const).map((r) => (
              <option key={r} value={r}>
                {REGIME_LABELS[r]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            htmlFor="fisc-ciiu"
            className="mb-1 block text-sm font-semibold"
          >
            Actividad económica (CIIU)
          </label>
          <input
            id="fisc-ciiu"
            autoComplete="off"
            inputMode="numeric"
            value={economicActivityCode}
            onChange={(e) =>
              setEconomicActivityCode(e.target.value.replace(/\D/g, '').slice(0, 10))
            }
            placeholder="5611"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 font-mono text-base"
          />
        </div>
      </div>

      <div className="mt-4">
        <label className="mb-1 block text-sm font-semibold">
          Códigos de responsabilidad DIAN
        </label>
        <p className="mb-2 text-xs text-fg-muted">
          Ej: <span className="font-mono">O-13</span> (Gran contribuyente),{' '}
          <span className="font-mono">R-99-PN</span> (No aplica). Consulta
          tu RUT para los códigos vigentes.
        </p>
        {codes.length > 0 ? (
          <ul className="mb-2 flex flex-wrap gap-2">
            {codes.map((code) => (
              <li
                key={code}
                className="flex items-center gap-1 rounded-full border border-border bg-surface-muted px-2 py-0.5 font-mono text-xs"
              >
                {code}
                <button
                  type="button"
                  onClick={() => removeCode(code)}
                  className="rounded-full p-0.5 text-fg-muted hover:bg-surface hover:text-fg"
                  aria-label={`Quitar ${code}`}
                >
                  <X className="size-3" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <div className="flex gap-2">
          <input
            autoComplete="off"
            value={codeDraft}
            onChange={(e) => setCodeDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCode();
              }
            }}
            placeholder="Ej: O-13"
            className="w-40 rounded-md border border-border bg-surface px-3 py-2 font-mono text-sm"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addCode}
            disabled={!codeDraft.trim()}
          >
            Agregar
          </Button>
        </div>
      </div>

      {save.error ? (
        <p className="mt-3 text-sm text-danger">
          {(save.error as Error).message}
        </p>
      ) : null}

      <div className="mt-4 flex justify-end">
        <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Guardando…' : 'Guardar datos fiscales'}
        </Button>
      </div>
    </CollapsibleSection>
  );
};
